import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, Menu, X } from "lucide-react";
import Logo from "./Logo";
import WhatsAppIcon from "./icons/WhatsAppIcon";

const NAV_LINKS = [
  { label: "How It Works", type: "anchor", target: "how-it-works" },
  { label: "Collection", type: "route", href: "/collection" },
  { label: "Why Aurevia", type: "anchor", target: "why" },
  { label: "Meet the Team", type: "anchor", target: "contact" },
];

const WHATSAPP_URL = "https://wa.me/918796564619";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = ({ onOpenWishlist, wishlistCount = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleNavClick = (link) => {
    setMobileOpen(false);
    if (link.type === "route") {
      navigate(link.href);
      return;
    }
    // anchor - scroll on home, else route home and scroll after mount
    if (location.pathname === "/") {
      scrollToId(link.target);
    } else {
      navigate("/");
      // wait for home to mount, then scroll
      setTimeout(() => scrollToId(link.target), 60);
    }
  };

  return (
    <>
      <header
        data-testid="top-navbar"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-brand-charcoal/85 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div
          className={`absolute bottom-0 left-0 right-0 h-px transition-opacity duration-500 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background:
              "linear-gradient(to right, transparent, rgba(201,169,97,0.35) 20%, rgba(201,169,97,0.35) 80%, transparent)",
          }}
        />

        <div className="editorial-container">
          <div className="flex h-20 md:h-24 items-center justify-between">
            <div className="flex-shrink-0">
              <Logo size="md" />
            </div>

            {/* Desktop nav links */}
            <nav
              aria-label="Primary"
              className="hidden md:flex items-center gap-10 lg:gap-12"
            >
              {NAV_LINKS.map((l) => {
                const isActive =
                  l.type === "route" && location.pathname.startsWith(l.href);
                return (
                  <button
                    key={l.label}
                    type="button"
                    onClick={() => handleNavClick(l)}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-current={isActive ? "page" : undefined}
                    className={`relative font-sans text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-brand-gold after:transition-all after:duration-300 ${
                      isActive
                        ? "text-brand-gold after:w-full"
                        : "text-brand-cream/70 hover:text-brand-gold after:w-0 hover:after:w-full"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </nav>

            {/* Right icons */}
            <div className="flex items-center gap-2 md:gap-3">
              <button
                type="button"
                onClick={onOpenWishlist}
                data-testid="nav-wishlist-button"
                aria-label="Open wishlist"
                className="relative h-10 w-10 flex items-center justify-center text-brand-cream/80 hover:text-brand-gold transition-colors"
              >
                <Heart className="h-[18px] w-[18px]" strokeWidth={1.5} />
                <span
                  data-testid="wishlist-count-badge"
                  className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-gold text-brand-charcoal text-[9px] font-sans font-semibold flex items-center justify-center"
                  aria-label={`${wishlistCount} items in wishlist`}
                >
                  {wishlistCount}
                </span>
              </button>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-whatsapp-link"
                aria-label="Open WhatsApp chat"
                className="hidden md:flex h-10 w-10 items-center justify-center text-brand-cream/80 hover:text-brand-gold transition-colors"
              >
                <WhatsAppIcon className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </a>

              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-open-button"
                aria-label="Open menu"
                className="md:hidden h-10 w-10 flex items-center justify-center text-brand-cream/90 hover:text-brand-gold transition-colors"
              >
                <Menu className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div aria-hidden className="h-20 md:h-24" />

      {/* Mobile overlay menu */}
      <div
        data-testid="mobile-menu-overlay"
        className={`md:hidden fixed inset-0 z-[60] transition-all duration-500 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <div className="absolute inset-0 bg-brand-charcoal" />
        <div className="relative h-full flex flex-col">
          <div className="flex h-20 items-center justify-between px-6">
            <Logo size="md" />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close-button"
              aria-label="Close menu"
              className="h-10 w-10 flex items-center justify-center text-brand-cream/90 hover:text-brand-gold transition-colors"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>

          <nav
            aria-label="Mobile primary"
            className="flex-1 flex flex-col justify-center px-8 gap-2"
          >
            {NAV_LINKS.map((l, i) => (
              <button
                key={l.label}
                type="button"
                onClick={() => handleNavClick(l)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group text-left py-5 border-b border-white/10 flex items-center justify-between transition-colors hover:text-brand-gold"
                style={{
                  animation: mobileOpen
                    ? `fade-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.06 + 0.1}s both`
                    : "none",
                }}
              >
                <span className="font-serif text-3xl font-light text-brand-cream group-hover:text-brand-gold transition-colors">
                  {l.label}
                </span>
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-cream/30">
                  0{i + 1}
                </span>
              </button>
            ))}
          </nav>

          <div className="px-8 pb-10 pt-6 flex flex-col gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-menu-whatsapp"
              className="font-sans text-xs uppercase tracking-[0.28em] text-brand-cream/60 hover:text-brand-gold transition-colors inline-flex items-center gap-3"
            >
              <WhatsAppIcon className="h-4 w-4" strokeWidth={1.5} />
              WhatsApp · +91 87965 64619
            </a>
            <p className="font-serif italic text-brand-gold/80 text-base">
              The quarry, at your doorstep.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
